import { MESSAGE_IMAGE_PREFIX, MESSAGE_IMAGE_SUFFIX, USER_STATUS_LEAVE, USER_STATUS_OFFLINE, USER_STATUS_ONLINE } from '../config/runtime';
import { resolveMediaUrl, type MediaProvider } from '@domain/media/resolve';

let localMessageCounter = 0;

export type ImageMessageMeta = {
  width?: number;
  height?: number;
  mimeType?: string;
  fileName?: string;
  size?: number;
};

export type ImageMessagePayload = {
  kind: 'image';
  version: 1;
  mediaId?: string;
  provider: MediaProvider | 'local';
  url: string;
  meta?: ImageMessageMeta;
};

export type AnyImagePayload = ImageMessagePayload;

const isAbsoluteHttpUrl = (value: string) => value.startsWith('http://') || value.startsWith('https://');

export function nextLocalMessageId() {
  localMessageCounter += 1;
  return `local-${Date.now()}-${localMessageCounter}`;
}

export function tsNow() {
  return new Date().toISOString().slice(11, 23);
}

export function getErrorMessage(error: unknown) {
  return error instanceof Error ? error.message : String(error);
}

export function formatClock(timestamp: number) {
  return new Date(timestamp).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  });
}

export function formatListTime(timestamp: number) {
  const date = new Date(timestamp);
  const now = new Date();
  return date.toISOString().slice(0, 10) === now.toISOString().slice(0, 10)
    ? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })
    : date.toLocaleDateString([], { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

export function formatTimelineDay(timestamp: number) {
  return new Date(timestamp).toLocaleDateString([], {
    month: 'long',
    day: 'numeric',
    weekday: 'short',
    timeZone: 'UTC',
  });
}

export function initials(value: string | undefined | null, fallback = 'LC') {
  const text = (value ?? '').trim();
  if (!text) {
    return fallback;
  }
  const parts = text.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return text.slice(0, 2).toUpperCase();
}

export function userStatusLabel(status: number) {
  if (status === USER_STATUS_ONLINE) {
    return '在线';
  }
  if (status === USER_STATUS_LEAVE) {
    return '离开';
  }
  if (status === USER_STATUS_OFFLINE) {
    return '离线';
  }
  return '状态未知';
}

export function wrapLocalImagePayload(url: string, meta?: ImageMessageMeta) {
  const payload: ImageMessagePayload = {
    kind: 'image',
    version: 1,
    provider: 'local',
    url,
    ...(meta ? { meta } : {}),
  };
  return `${MESSAGE_IMAGE_PREFIX}${JSON.stringify(payload)}${MESSAGE_IMAGE_SUFFIX}`;
}

export function wrapImagePayload(input: { mediaId: string; provider: MediaProvider; url: string; meta?: ImageMessageMeta }) {
  if (input.provider !== 's3') {
    throw new Error('Image payload provider must be s3');
  }
  if (!isAbsoluteHttpUrl(input.url)) {
    throw new Error('S3 image payload URL must be absolute');
  }
  const payload: ImageMessagePayload = {
    kind: 'image',
    version: 1,
    mediaId: input.mediaId,
    provider: input.provider,
    url: input.url,
    ...(input.meta ? { meta: input.meta } : {}),
  };
  return `${MESSAGE_IMAGE_PREFIX}${JSON.stringify(payload)}${MESSAGE_IMAGE_SUFFIX}`;
}

export function extractImagePayload(text: string): AnyImagePayload | null {
  if (!isImageMessageEnvelope(text)) {
    return null;
  }
  const raw = text.slice(MESSAGE_IMAGE_PREFIX.length, text.length - MESSAGE_IMAGE_SUFFIX.length);
  try {
    const parsed = JSON.parse(raw) as Partial<AnyImagePayload>;
    if (
      parsed.kind === 'image'
      && parsed.version === 1
      && typeof parsed.url === 'string'
      && parsed.url.length > 0
      && (
        (parsed.provider === 's3' && isAbsoluteHttpUrl(parsed.url))
        || (parsed.provider === 'local' && (parsed.url.startsWith('blob:') || parsed.url.startsWith('data:')))
      )
    ) {
      return {
        kind: 'image',
        version: 1,
        mediaId: typeof parsed.mediaId === 'string' ? parsed.mediaId : undefined,
        provider: parsed.provider,
        url: parsed.url,
        meta: parsed.meta,
      } satisfies ImageMessagePayload;
    }
  } catch {
    return null;
  }
  return null;
}

export function isImageMessageEnvelope(text: string) {
  return text.startsWith(MESSAGE_IMAGE_PREFIX) && text.endsWith(MESSAGE_IMAGE_SUFFIX);
}

export function resolveImageUrl(pathOrPayload: AnyImagePayload, cdnBase: string) {
  return resolveMediaUrl(pathOrPayload, cdnBase, { allowLocalPreview: pathOrPayload.provider === 'local' });
}

export function imageAspectRatio(meta: ImageMessageMeta | undefined) {
  if (!meta?.width || !meta?.height || meta.width <= 0 || meta.height <= 0) {
    return undefined;
  }
  return `${meta.width} / ${meta.height}`;
}

export function imageBubbleWidth(meta: ImageMessageMeta | undefined) {
  if (!meta?.width || !meta?.height || meta.width <= 0 || meta.height <= 0) {
    return 'min(320px, 62vw)';
  }
  const aspect = meta.width / meta.height;
  if (aspect >= 1.4) {
    return 'min(320px, 62vw)';
  }
  if (aspect <= 0.8) {
    return 'min(220px, 48vw)';
  }
  return 'min(280px, 56vw)';
}

export async function readBrowserImageMeta(file: File): Promise<ImageMessageMeta> {
  const objectUrl = URL.createObjectURL(file);
  try {
    const dimensions = await new Promise<{ width: number; height: number }>((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve({
        width: image.naturalWidth || image.width,
        height: image.naturalHeight || image.height,
      });
      image.onerror = () => reject(new Error('图片尺寸读取失败'));
      image.src = objectUrl;
    });

    return {
      width: dimensions.width,
      height: dimensions.height,
      mimeType: file.type || undefined,
      fileName: file.name || undefined,
      size: Number.isFinite(file.size) ? file.size : undefined,
    };
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export function fallbackImageMeta(file: File): ImageMessageMeta {
  return {
    mimeType: file.type || undefined,
    fileName: file.name || undefined,
    size: Number.isFinite(file.size) ? file.size : undefined,
  };
}
